import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "../data/site";
import { ORDER_URL } from "../data/menu";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Menu", href: "#menu" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="header-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-brand-cream/85 backdrop-blur-xl shadow-[0_8px_32px_-12px_rgba(0,0,0,0.12)] border-b border-brand-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
        <a href="#home" className="flex items-center group" data-testid="brand-logo">
          <img 
            src="/logo.png" 
            alt="Red Chilly's Logo" 
            className="h-10 md:h-12 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="font-heading uppercase text-sm tracking-[0.15em] font-bold text-brand-charcoal hover:text-brand-red transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 w-0 h-[2px] bg-brand-red group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SITE.phoneHref}
            className="hidden md:flex items-center gap-2 font-heading uppercase text-xs tracking-[0.15em] font-bold text-brand-charcoal hover:text-brand-red transition-colors"
            data-testid="header-phone"
          >
            <Phone size={14} strokeWidth={2.5} />
            {SITE.phone}
          </a>
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-online-cta"
            className="hidden sm:inline-flex items-center justify-center px-6 py-3 rounded-full bg-brand-red text-brand-cream font-heading uppercase text-xs tracking-[0.18em] font-bold hover:bg-brand-deepred glow-red-hover transition-all hover:-translate-y-0.5 active:translate-y-0 shadow-[0_8px_24px_-8px_rgba(179,0,27,0.5)]"
          >
            Order Online
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 rounded-full bg-brand-charcoal text-brand-cream flex items-center justify-center"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-brand-cream border-t border-brand-border overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-heading uppercase text-base tracking-[0.15em] font-bold text-brand-charcoal hover:text-brand-red transition-colors py-2 border-b border-brand-border/60"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex justify-center px-6 py-3 rounded-full bg-brand-red text-brand-cream font-heading uppercase text-sm tracking-[0.18em] font-bold"
                data-testid="mobile-order-cta"
              >
                Order Online
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
